import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function CategoriesSuppliers() {
    const [tab, setTab] = useState("categories");
    return (
        <div className="space-y-6" data-testid="catalogos-page">
            <header>
                <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Catálogos</p>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Categorías y Proveedores</h1>
                <p className="text-sm text-slate-500 mt-1">Organiza tus gastos e ingresos y mantén una base de proveedores.</p>
            </header>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="categories" data-testid="tab-categories">Categorías</TabsTrigger>
                    <TabsTrigger value="suppliers" data-testid="tab-suppliers">Proveedores</TabsTrigger>
                </TabsList>
                <TabsContent value="categories" className="mt-4">
                    <CategoriesPanel />
                </TabsContent>
                <TabsContent value="suppliers" className="mt-4">
                    <SuppliersPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function CategoriesPanel() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const empty = { name: "", kind: "expense", classification: "operativo", color: "#0F172A" };
    const [form, setForm] = useState(empty);

    const load = () => api.get("/categories").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (c) => {
        setEditing(c);
        setForm({ name: c.name, kind: c.kind, classification: c.classification || "operativo", color: c.color || "#0F172A" });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.name) return toast.error("Nombre requerido");
        try {
            if (editing) await api.put(`/categories/${editing.id}`, form);
            else await api.post("/categories", form);
            toast.success("Guardado");
            setOpen(false); load();
        } catch { toast.error("Error"); }
    };
    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar categoría ${c.name}?`)) return;
        await api.delete(`/categories/${c.id}`);
        toast.success("Eliminada"); load();
    };

    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-display font-medium">Categorías ({items.length})</CardTitle>
                {canWrite && (
                    <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-category-button">
                        <Plus size={16} className="mr-1" /> Nueva
                    </Button>
                )}
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                <th className="text-left px-4 py-3 font-semibold">Color</th>
                                <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                                <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                                <th className="text-left px-4 py-3 font-semibold">Clasificación</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={5} className="text-center px-4 py-10 text-slate-400">Sin categorías</td></tr>}
                            {items.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100" data-testid={`row-cat-${c.id}`}>
                                    <td className="px-4 py-3">
                                        <span className="inline-block w-4 h-4 rounded-full border border-slate-200" style={{ background: c.color || "#0F172A" }} />
                                    </td>
                                    <td className="px-4 py-3 text-slate-900 font-medium">{c.name}</td>
                                    <td className="px-4 py-3">
                                        {c.kind === "income"
                                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ingreso</Badge>
                                            : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Gasto</Badge>}
                                    </td>
                                    <td className="px-4 py-3 capitalize text-slate-700">{c.classification || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            {canWrite && (
                                                <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}><Pencil size={14} /></button>
                                            )}
                                            {isAdmin && (
                                                <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(c)} data-testid={`del-cat-${c.id}`}><Trash2 size={14} /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Tipo</Label>
                                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="income">Ingreso</SelectItem>
                                        <SelectItem value="expense">Gasto</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs">Clasificación</Label>
                                <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="operativo">Operativo</SelectItem>
                                        <SelectItem value="administrativo">Administrativo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Color</Label>
                            <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20 p-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-category">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function SuppliersPanel() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const empty = { name: "", contact: "", phone: "", notes: "" };
    const [form, setForm] = useState(empty);

    const load = () => api.get("/suppliers").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (s) => {
        setEditing(s);
        setForm({ name: s.name, contact: s.contact || "", phone: s.phone || "", notes: s.notes || "" });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.name) return toast.error("Nombre requerido");
        try {
            if (editing) await api.put(`/suppliers/${editing.id}`, form);
            else await api.post("/suppliers", form);
            toast.success("Guardado");
            setOpen(false); load();
        } catch { toast.error("Error"); }
    };
    const remove = async (s) => {
        if (!window.confirm(`¿Eliminar proveedor ${s.name}?`)) return;
        await api.delete(`/suppliers/${s.id}`);
        toast.success("Eliminado"); load();
    };

    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-display font-medium">Proveedores ({items.length})</CardTitle>
                {canWrite && (
                    <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-supplier-button">
                        <Plus size={16} className="mr-1" /> Nuevo
                    </Button>
                )}
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                                <th className="text-left px-4 py-3 font-semibold">Contacto</th>
                                <th className="text-left px-4 py-3 font-semibold">Teléfono</th>
                                <th className="text-left px-4 py-3 font-semibold">Notas</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={5} className="text-center px-4 py-10 text-slate-400">Sin proveedores</td></tr>}
                            {items.map((s) => (
                                <tr key={s.id} className="border-t border-slate-100" data-testid={`row-sup-${s.id}`}>
                                    <td className="px-4 py-3 text-slate-900 font-medium">{s.name}</td>
                                    <td className="px-4 py-3 text-slate-700">{s.contact || "—"}</td>
                                    <td className="px-4 py-3 text-slate-700">{s.phone || "—"}</td>
                                    <td className="px-4 py-3 text-slate-500">{s.notes || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            {canWrite && (
                                                <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(s)} data-testid={`edit-sup-${s.id}`}><Pencil size={14} /></button>
                                            )}
                                            {isAdmin && (
                                                <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(s)} data-testid={`del-sup-${s.id}`}><Trash2 size={14} /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Contacto</Label>
                                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
                            </div>
                            <div>
                                <Label className="text-xs">Teléfono</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Notas</Label>
                            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-supplier">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
